'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface PricingPageClientProps {
  session: any;
}

const PRICING_PACKAGES = [
  {
    type: 'credits10',
    name: 'Starter',
    credits: 10,
    price: '$15',
    pricePerBook: '$1.50',
    features: ['10 credits ($1.50/credit)', '10 coloring books or', '3 audio picture books', 'No watermarks', 'Forever access'],
  },
  {
    type: 'credits25',
    name: 'Family',
    credits: 25,
    price: '$30',
    pricePerBook: '$1.20',
    features: ['25 credits ($1.20/credit)', '25 coloring books or', '8 audio picture books', 'No watermarks', 'Forever access'],
    featured: true,
  },
  {
    type: 'credits60',
    name: 'Pro',
    credits: 60,
    price: '$60',
    pricePerBook: '$1.00',
    features: ['60 credits ($1.00/credit)', '60 coloring books or', '20 audio picture books', 'No watermarks', 'Forever access'],
  },
];

const SUBSCRIPTIONS = [
  {
    id: 'amaya_plus',
    name: 'Amaya Plus',
    price: '$19.99/mo',
    credits: 30,
    features: [
      '30 credits per month',
      '~10 audio books or 30 coloring books',
      '100+ audio story gallery access',
      'No watermarks on all creations',
      'Priority customer support',
      'Cancel anytime'
    ],
    description: 'Perfect for families',
  },
  {
    id: 'amaya_pro',
    name: 'Amaya Pro',
    price: '$49.99/mo',
    credits: 100,
    features: [
      '100 credits per month',
      '~33 audio books or 100 coloring books',
      'Everything in Amaya Plus',
      'Custom voice selection',
      'Bilingual story mode',
      'Early access to new features',
      'Priority generation queue'
    ],
    featured: true,
    description: 'Best for educators',
  },
];

export function PricingPageClient({ session }: PricingPageClientProps) {
  const router = useRouter();
  const [loading, setLoading] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<'NONE' | 'AMAYA_PLUS' | 'AMAYA_PRO'>('NONE');

  // Fetch subscription status
  useEffect(() => {
    if (session?.user) {
      fetch('/api/account')
        .then(res => res.json())
        .then(data => {
          setSubscriptionPlan(data.subscriptionPlan || 'NONE');
        })
        .catch(err => console.error('Error fetching account data:', err));
    }
  }, [session]);

  const proceedToPurchase = async (packageType: string) => {
    setLoading(packageType);
    try {
      const response = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packId: packageType }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start checkout');
        setLoading(null);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const proceedToSubscription = async (plan: 'amaya_plus' | 'amaya_pro') => {
    setLoading(plan);
    try {
      const response = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start subscription');
        setLoading(null);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
      setLoading(null);
    }
  };

  const handlePurchase = async (packageType: string) => {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ type: 'credits', packageType }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }
    await proceedToPurchase(packageType);
  };

  const handleSubscribe = async (plan: 'amaya_plus' | 'amaya_pro') => {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ type: 'subscription', plan }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.href);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }
    await proceedToSubscription(plan);
  };

  // Helper function to check if a plan is the user's current plan
  const isCurrentPlan = (planId: string) => {
    return subscriptionPlan === planId.toUpperCase();
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '100px',
      paddingBottom: '60px',
      background: 'var(--color-bg-secondary)',
    }}>
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes buttonShine {
          0% { left: -100%; }
          50%, 100% { left: 100%; }
        }

        @media (max-width: 768px) {
          #credit-packages-grid {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
            padding: 0 1rem;
          }
          
          #subscriptions-grid {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
            padding: 0 1rem;
          }

          .pricing-card-2026,
          .subscription-card-2026 {
            transform: scale(1) !important;
          }
        }

        @media (max-width: 640px) {
          #credit-packages-grid,
          #subscriptions-grid {
            padding: 0 0.5rem;
          }
        }
      `}</style>
      
      {/* Pricing Section - 2026 Award-Winning Design */}
      <div id="pricing-section" style={{ 
        position: 'relative',
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: 'clamp(2.5rem, 5vw, 4rem) 1.5rem', 
        scrollMarginTop: '80px',
        overflow: 'hidden',
      }}>
        {/* Ambient Background Elements */}
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '-8%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(168, 216, 230, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          filter: 'blur(50px)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '-8%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(201, 182, 228, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          filter: 'blur(50px)',
        }} />

        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 3rem)', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'clamp(2.25rem, 6vw, 4rem)',
            fontWeight: 900,
            background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '1.25rem',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
          }}>
            Start Creating Magical Stories
          </h2>
          <p style={{
            fontSize: 'clamp(1.125rem, 2.2vw, 1.5rem)',
            color: '#64748b',
            fontWeight: 500,
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Unlock unlimited creativity with a monthly subscription, or buy credits as you go
          </p>
        </div>

        {/* Subscription Plans - Monthly */}
        <div 
          id="subscriptions-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'clamp(1.25rem, 2vw, 1.75rem)',
            maxWidth: '850px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 'clamp(2rem, 3vw, 3rem)',
            position: 'relative',
            zIndex: 1,
          }}>
          {SUBSCRIPTIONS.map((sub, index) => (
            <div 
              key={sub.id}
              className="subscription-card-2026"
              style={{ 
                background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                borderRadius: '24px',
                padding: 'clamp(1.5rem, 2.5vw, 2rem)',
            textAlign: 'center',
                boxShadow: sub.featured
                  ? '0 12px 48px rgba(102, 126, 234, 0.2), 0 4px 16px rgba(118, 75, 162, 0.12)'
                  : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                border: sub.featured ? '2px solid rgba(102, 126, 234, 0.4)' : '1px solid rgba(0, 0, 0, 0.04)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transform: sub.featured ? 'scale(1.03)' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = sub.featured ? 'scale(1.06) translateY(-16px)' : 'translateY(-16px) scale(1.04)';
                e.currentTarget.style.boxShadow = sub.featured
                  ? '0 32px 90px rgba(102, 126, 234, 0.45), 0 16px 48px rgba(118, 75, 162, 0.3), 0 0 60px rgba(102, 126, 234, 0.2)'
                  : '0 24px 70px rgba(248, 200, 160, 0.35), 0 12px 36px rgba(232, 198, 180, 0.25)';
                e.currentTarget.style.borderColor = sub.featured ? 'rgba(102, 126, 234, 0.7)' : 'rgba(248, 200, 160, 0.5)';
                const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                if (glow) glow.style.opacity = '1';
                const icon = e.currentTarget.querySelector('.subscription-icon') as HTMLElement;
                if (icon) {
                  icon.style.transform = 'scale(1.15) rotate(5deg)';
                  icon.style.boxShadow = sub.featured
                    ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(102, 126, 234, 0.5)'
                    : '0 16px 50px rgba(248, 200, 160, 0.6), 0 8px 20px rgba(248, 200, 160, 0.5)';
                }
                const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                if (iconGlow) iconGlow.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = sub.featured ? 'scale(1.03)' : 'scale(1)';
                e.currentTarget.style.boxShadow = sub.featured
                  ? '0 12px 48px rgba(102, 126, 234, 0.2), 0 4px 16px rgba(118, 75, 162, 0.12)'
                  : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
                e.currentTarget.style.borderColor = sub.featured ? 'rgba(102, 126, 234, 0.4)' : 'rgba(0, 0, 0, 0.04)';
                const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                if (glow) glow.style.opacity = '0';
                const icon = e.currentTarget.querySelector('.subscription-icon') as HTMLElement;
                if (icon) {
                  icon.style.transform = 'scale(1) rotate(0deg)';
                  icon.style.boxShadow = sub.featured
                    ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)';
                }
                const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                if (iconGlow) iconGlow.style.opacity = '0';
              }}
            >
              {/* Hover Glow Effect */}
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: sub.featured
                  ? 'radial-gradient(circle at center, rgba(102, 126, 234, 0.12) 0%, transparent 70%)'
                  : 'radial-gradient(circle at center, rgba(232, 198, 180, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />

              {/* Featured Badge */}
              {sub.featured && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                  padding: '0.375rem 0.875rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '10px',
                  fontSize: '0.6875rem',
                  fontWeight: 800,
                color: 'white',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}>
                  <span style={{ fontSize: '0.75rem' }}>👑</span>
                POPULAR
              </div>
              )}
              
              {/* Icon Badge */}
              <div 
                className="subscription-icon"
                style={{
                  width: 'clamp(70px, 14vw, 90px)',
                  height: 'clamp(70px, 14vw, 90px)',
                  borderRadius: '24px',
                  background: sub.featured
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: sub.featured
                    ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)',
                  position: 'relative',
                  transform: 'scale(1)',
                  transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  overflow: 'hidden',
                }}
              >
                {/* Animated Background Glow */}
                <div className="icon-glow" style={{
                  position: 'absolute',
                  inset: '-20%',
                  background: sub.featured
                    ? 'radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%)'
                    : 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.6s ease',
                  animation: 'pulse 3s ease-in-out infinite',
                }} />
                
                {/* SVG Icon */}
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {sub.featured ? (
                    // Amaya Pro - Crown icon
                    <>
                      <path d="M2 20h20v2H2z" fill="white" fillOpacity="0.9"/>
                      <path d="M12 2L15 9L22 10L17 15L18 22L12 19L6 22L7 15L2 10L9 9L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                    </>
                  ) : (
                    // Amaya Plus - Heart icon
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                  )}
                </svg>
            </div>

              {/* Plan Name */}
              <h3 style={{
                fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '0.75rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                {sub.name}
              </h3>
              
              {/* Price */}
              <div style={{ marginBottom: '0.625rem' }}>
              <div style={{
                  fontSize: 'clamp(2.25rem, 4.5vw, 2.75rem)',
                  fontWeight: 900,
                  background: sub.featured
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1,
                  marginBottom: '0.5rem',
                }}>
                  {sub.price}
                </div>
                <div style={{
                  fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                  fontWeight: 700,
                  color: '#64748b',
                  marginBottom: '0.25rem',
                }}>
                  {sub.credits} Credits Monthly
                </div>
                <div style={{
                  fontSize: 'clamp(0.8125rem, 1.3vw, 0.875rem)',
                  color: '#94a3b8',
                  fontWeight: 500,
                }}>
                  {sub.description}
                </div>
              </div>

              {/* Features */}
              <ul style={{
                listStyle: 'none',
                padding: 0,
                marginBottom: '1.5rem',
                textAlign: 'left',
                marginTop: '1.25rem',
              }}>
                {sub.features.slice(0, 4).map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    marginBottom: '0.625rem',
                    fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                    color: '#475569',
                    fontWeight: 500,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" fill="#10b981" fillOpacity="0.15"/>
                      <path d="M8 12.5l2.5 2.5L16 9" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handleSubscribe(sub.id as 'amaya_plus' | 'amaya_pro')}
                disabled={loading === sub.id || isCurrentPlan(sub.id)}
                className="subscription-cta-btn"
                style={{
                  width: '100%',
                  padding: 'clamp(0.875rem, 2vw, 1.125rem) 2rem',
                  background: sub.featured
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                  backgroundSize: '200% 100%',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                  fontWeight: 900,
                  cursor: (loading === sub.id || isCurrentPlan(sub.id)) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: sub.featured
                    ? '0 10px 35px rgba(102, 126, 234, 0.5), 0 4px 15px rgba(118, 75, 162, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 8px 28px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(232, 198, 180, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                  opacity: isCurrentPlan(sub.id) ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (loading !== sub.id && !isCurrentPlan(sub.id)) {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.backgroundPosition = '100% 0';
                    e.currentTarget.style.boxShadow = sub.featured
                      ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 25px rgba(118, 75, 162, 0.5), 0 0 40px rgba(102, 126, 234, 0.3)'
                      : '0 14px 45px rgba(248, 200, 160, 0.6), 0 8px 20px rgba(232, 198, 180, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.backgroundPosition = '0% 0';
                  e.currentTarget.style.boxShadow = sub.featured
                    ? '0 10px 35px rgba(102, 126, 234, 0.5), 0 4px 15px rgba(118, 75, 162, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 8px 28px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(232, 198, 180, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                }}
              >
                {/* Button Shine Effect */}
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  animation: 'buttonShine 3s ease-in-out infinite',
                  pointerEvents: 'none',
                }} />
                
                {loading === sub.id ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem', position: 'relative', zIndex: 1 }}>
                    <span className="spinner" style={{ 
                      display: 'inline-block',
                      width: '1.125rem',
                      height: '1.125rem',
                      border: '2.5px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}></span>
                    Processing...
                  </span>
                ) : isCurrentPlan(sub.id) ? (
                  <span style={{ position: 'relative', zIndex: 1 }}>Current Plan ✓</span>
                ) : (subscriptionPlan === 'AMAYA_PRO' && sub.id === 'amaya_plus') ? (
                  <span style={{ position: 'relative', zIndex: 1 }}>Downgrade to Plus</span>
                ) : (subscriptionPlan === 'AMAYA_PLUS' && sub.id === 'amaya_pro') ? (
                  <span style={{ position: 'relative', zIndex: 1 }}>Upgrade to Pro</span>
                ) : (
                  <span style={{ position: 'relative', zIndex: 1 }}>Subscribe →</span>
                )}
              </button>
            </div>
          ))}
          </div>
        {/* Credit Packages - One-Time Payment */}
        <div 
          id="credit-packages-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'clamp(1.25rem, 2vw, 1.75rem)',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 'clamp(2rem, 3vw, 3rem)',
            position: 'relative',
            zIndex: 1,
          }}>
          {PRICING_PACKAGES.map((pkg, index) => (
            <div
              key={pkg.type}
              className="pricing-card-2026"
              style={{
                background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                borderRadius: '24px',
                padding: 'clamp(1.5rem, 2.5vw, 2rem)',
                textAlign: 'center',
                boxShadow: pkg.featured
                  ? '0 12px 48px rgba(168, 216, 230, 0.2), 0 4px 16px rgba(201, 182, 228, 0.12)'
                  : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                border: pkg.featured ? '2px solid rgba(168, 216, 230, 0.4)' : '1px solid rgba(0, 0, 0, 0.04)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transform: pkg.featured ? 'scale(1.04)' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.featured ? 'scale(1.08) translateY(-16px)' : 'translateY(-16px) scale(1.04)';
                e.currentTarget.style.boxShadow = pkg.featured
                  ? '0 32px 90px rgba(168, 216, 230, 0.45), 0 16px 48px rgba(201, 182, 228, 0.3), 0 0 60px rgba(168, 216, 230, 0.2)'
                  : '0 24px 70px rgba(168, 216, 230, 0.25), 0 12px 36px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.borderColor = pkg.featured ? 'rgba(168, 216, 230, 0.7)' : 'rgba(168, 216, 230, 0.4)';
                const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                if (glow) glow.style.opacity = '1';
                const icon = e.currentTarget.querySelector('.pricing-icon') as HTMLElement;
                if (icon) {
                  icon.style.transform = 'scale(1.15) rotate(5deg)';
                  icon.style.boxShadow = index === 0
                    ? '0 16px 50px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(102, 126, 234, 0.5)'
                    : index === 1
                    ? '0 16px 50px rgba(168, 216, 230, 0.7), 0 8px 20px rgba(201, 182, 228, 0.6)'
                    : '0 16px 50px rgba(248, 200, 160, 0.6), 0 8px 20px rgba(248, 200, 160, 0.5)';
                }
                const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                if (iconGlow) iconGlow.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.featured ? 'scale(1.04)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.featured
                  ? '0 12px 48px rgba(168, 216, 230, 0.2), 0 4px 16px rgba(201, 182, 228, 0.12)'
                  : '0 8px 32px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.02)';
                e.currentTarget.style.borderColor = pkg.featured ? 'rgba(168, 216, 230, 0.4)' : 'rgba(0, 0, 0, 0.04)';
                const glow = e.currentTarget.querySelector('.card-glow') as HTMLElement;
                if (glow) glow.style.opacity = '0';
                const icon = e.currentTarget.querySelector('.pricing-icon') as HTMLElement;
                if (icon) {
                  icon.style.transform = 'scale(1) rotate(0deg)';
                  icon.style.boxShadow = index === 0
                    ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : index === 1
                    ? '0 12px 40px rgba(168, 216, 230, 0.5), 0 4px 12px rgba(201, 182, 228, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)';
                }
                const iconGlow = e.currentTarget.querySelector('.icon-glow') as HTMLElement;
                if (iconGlow) iconGlow.style.opacity = '0';
              }}
            >
              {/* Hover Glow Effect */}
              <div className="card-glow" style={{
                position: 'absolute',
                inset: 0,
                background: pkg.featured
                  ? 'radial-gradient(circle at center, rgba(168, 216, 230, 0.12) 0%, transparent 70%)'
                  : 'radial-gradient(circle at center, rgba(168, 216, 230, 0.08) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
              }} />

              {/* Featured Badge */}
              {pkg.featured && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                  padding: '0.375rem 0.875rem',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  borderRadius: '10px',
                  fontSize: '0.6875rem',
                  fontWeight: 800,
                color: 'white',
                  letterSpacing: '0.05em',
                  boxShadow: '0 4px 16px rgba(255, 215, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}>
                  <span style={{ fontSize: '0.75rem' }}>⭐</span>
                BEST VALUE
              </div>
              )}
              
              {/* Icon Badge */}
              <div 
                className="pricing-icon"
                style={{
                  width: 'clamp(70px, 14vw, 90px)',
                  height: 'clamp(70px, 14vw, 90px)',
                  borderRadius: '24px',
                  background: index === 0
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : index === 1
                    ? 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)'
                    : 'linear-gradient(135deg, #F8C8A0 0%, #E8C6B4 100%)',
                    display: 'flex',
                    alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: index === 0
                    ? '0 12px 40px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(102, 126, 234, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : index === 1
                    ? '0 12px 40px rgba(168, 216, 230, 0.5), 0 4px 12px rgba(201, 182, 228, 0.4), inset 0 2px 8px rgba(255, 255, 255, 0.4)'
                    : '0 12px 40px rgba(248, 200, 160, 0.4), 0 4px 12px rgba(248, 200, 160, 0.3), inset 0 2px 8px rgba(255, 255, 255, 0.4)',
                  position: 'relative',
                  transform: 'scale(1)',
                  transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  overflow: 'hidden',
                }}
              >
                {/* Animated Background Glow */}
                <div className="icon-glow" style={{
                  position: 'absolute',
                  inset: '-20%',
                  background: pkg.featured
                    ? 'radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%)'
                    : 'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.6s ease',
                  animation: 'pulse 3s ease-in-out infinite',
                }} />
                
                {/* SVG Icon */}
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {index === 0 && (
                    // Starter - Rocket/Launch icon
                    <path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                  )}
                  {index === 1 && (
                    // Family - Heart icon
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                  )}
                  {index === 2 && (
                    // Pro - Crown icon
                    <>
                      <path d="M2 20h20v2H2z" fill="white" fillOpacity="0.9"/>
                      <path d="M12 2L15 9L22 10L17 15L18 22L12 19L6 22L7 15L2 10L9 9L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.9"/>
                    </>
                  )}
                </svg>
            </div>

              {/* Plan Name */}
              <h3 style={{
                fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: '0.75rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                {pkg.name}
              </h3>
              
              {/* Price */}
              <div style={{ marginBottom: '0.625rem' }}>
                <div style={{
                  fontSize: 'clamp(2.25rem, 4.5vw, 2.75rem)',
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1,
                  marginBottom: '0.5rem',
                }}>
                  {pkg.price}
                </div>
                <div style={{
                  fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                  fontWeight: 700,
                  color: '#64748b',
                  marginBottom: '0.25rem',
                }}>
                  {pkg.credits} Credits
                </div>
                <div style={{
                  fontSize: 'clamp(0.8125rem, 1.3vw, 0.875rem)',
                  color: '#94a3b8',
                  fontWeight: 500,
                }}>
                  {pkg.pricePerBook} per story
                </div>
              </div>

              {/* Features */}
              <ul style={{
                listStyle: 'none',
                padding: 0,
                marginBottom: '1.5rem',
                textAlign: 'left',
                marginTop: '1.25rem',
              }}>
                {pkg.features.slice(0, 3).map((feature, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    marginBottom: '0.625rem',
                    fontSize: 'clamp(0.875rem, 1.5vw, 0.9375rem)',
                    color: '#475569',
                    fontWeight: 500,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" fill="#10b981" fillOpacity="0.15"/>
                      <path d="M8 12.5l2.5 2.5L16 9" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handlePurchase(pkg.type)}
                disabled={loading === pkg.type}
                className="pricing-cta-btn"
                style={{
                  width: '100%',
                  padding: 'clamp(0.875rem, 2vw, 1.125rem) 2rem',
                  background: pkg.featured
                    ? 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundSize: '200% 100%',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                  fontWeight: 900,
                  cursor: loading === pkg.type ? 'not-allowed' : 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: pkg.featured
                    ? '0 10px 35px rgba(168, 216, 230, 0.5), 0 4px 15px rgba(201, 182, 228, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 8px 28px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(118, 75, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  position: 'relative',
                  overflow: 'hidden',
                  isolation: 'isolate',
                }}
                onMouseEnter={(e) => {
                  if (loading !== pkg.type) {
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                    e.currentTarget.style.backgroundPosition = '100% 0';
                    e.currentTarget.style.boxShadow = pkg.featured
                      ? '0 16px 50px rgba(168, 216, 230, 0.6), 0 8px 25px rgba(201, 182, 228, 0.5), 0 0 40px rgba(168, 216, 230, 0.3)'
                      : '0 14px 45px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(118, 75, 162, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.backgroundPosition = '0% 0';
                  e.currentTarget.style.boxShadow = pkg.featured
                    ? '0 10px 35px rgba(168, 216, 230, 0.5), 0 4px 15px rgba(201, 182, 228, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    : '0 8px 28px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(118, 75, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                }}
              >
                {/* Button Shine Effect */}
                <span style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  animation: 'buttonShine 3s ease-in-out infinite',
                  pointerEvents: 'none',
                }} />
                
                {loading === pkg.type ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem', position: 'relative', zIndex: 1 }}>
                    <span className="spinner" style={{ 
                      display: 'inline-block',
                      width: '1.125rem',
                      height: '1.125rem',
                      border: '2.5px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}></span>
                    Processing...
                  </span>
                ) : (
                  <span style={{ position: 'relative', zIndex: 1 }}>Get Started →</span>
                )}
              </button>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
